import React, { useState, useEffect, useRef } from "react";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { useI18n } from "intl/useI18n";
import StickersTabs from "./Stickers/StickersTabs.vue";
import StickersToolsBorder from "./Stickers/StickersTools/StickersToolsBorder.vue";
import StickersToolsBackground from "./Stickers/StickersTools/StickersToolsBackground.vue";
import ArtworkEnhancementFeedback from "components/jiffy/react-dtf-shared/ArtworkEnhancementFeedback.vue";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import ArtworkImage from "./ArtworkImage.vue";
import DesignServicePromo from "./DesignServicePromo.vue";
import ArtworkQualityConfigPanel from "./ArtworkQualityConfigPanel.vue";
import ArtworkQualityConfigPanelAbAiMonetization from "./ArtworkQualityConfigPanel/ArtworkQualityConfigPanel.vue";
import ArtworkDimensions from "./ArtworkDimensions.vue";
import ArtworkMultiDimensions from "./ArtworkMultiSize/ArtworkMultiDimensions.vue";
import ArtworkQuantity from "./ArtworkQuantity.vue";
import ArtworkInfo from "./ArtworkInfo.vue";
import TrimInfo from "./TrimInfo.vue";
import ArtworkError from "./ArtworkError.vue";
import ArtworkNotification from "./ArtworkNotification.vue";
import ReuploadBox from "./ReuploadBox.vue";
import RedrawNotes from "./RedrawNotes.vue";
import MultiSizeTotal from "./ArtworkMultiSize/MultiSizeTotal.vue";
import { useDtfStore } from "./store";
import { cn } from "tailwind-variants";
import "./Artwork.scss";

const TABS = [
  {
    label: "Background",
    icon: "tools-background",
    value: "background"
  },
  {
    label: "Border",
    icon: "tools-border",
    value: "border"
  }
];

export default function Artwork({
  artwork = null,
  index,
  currentStore = null,
  isMultiUpload = false,
  showFullScreenButton = false,
  defaultActiveConfigSection = "jiffy-print-perfect",
  showFreeTransfersBadge = false,
  ...restProps
}) {
  const { t } = useI18n();
  const dtfStore = useDtfStore();

  const [width, setWidth] = useState(null);
  const [height, setHeight] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [activeConfigSection, setActiveConfigSection] = useState(defaultActiveConfigSection);

  const rootRef = useRef(null);
  const fileRef = useRef(null);
  const redrawNotesRef = useRef(null);

  const sizeMessageMaxSize = 10;

  function printAsIs() {
    return dtfStore.printAsIs;
  }

  function pdpType() {
    return dtfStore.pdpType;
  }

  function sizeErrorTypes() {
    return dtfStore.sizeErrorTypes;
  }

  function sticker() {
    return dtfStore.sticker;
  }

  function minStickerQuantity() {
    return dtfStore.minStickerQuantity;
  }

  function isSpangle() {
    return dtfStore.isSpangle;
  }

  function isSticker() {
    return dtfStore.isSticker;
  }

  function experiments() {
    return dtfStore.experiments;
  }

  function showRedrawUI() {
    return dtfStore.showRedrawUI;
  }

  function showMultiSize() {
    return dtfStore.showMultiSize;
  }

  function isStandardArtwork() {
    return dtfStore.isStandardArtwork;
  }

  function enableAbAiMonetization() {
    return dtfStore.enableAbAiMonetization;
  }

  function reorderEnabled() {
    return dtfStore.reorderEnabled;
  }

  function paidExpertEditAddonEnabled() {
    return dtfStore.paidExpertEditAddonEnabled;
  }

  function lastConfirmedConfigSection() {
    return dtfStore.lastConfirmedConfigSection;
  }

  function metadata() {
    return artwork?.metadata;
  }

  function withError() {
    return (
      metadata()?.errors?.length > 0 ||
      artwork?.errorMessage ||
      artwork?.uploadError ||
      artwork?.retryFailed
    );
  }

  function isMultiSizeError() {
    return artwork?.isMultiSizeError;
  }

  function hasArtwork() {
    return artwork && !artwork.isSimulated && !artwork.uploadError;
  }

  function hasErrorMessage() {
    const baseCondition = artwork && !artwork.isSimulated;
    return baseCondition && withError() && !isMultiSizeError();
  }

  function isMinQuantityInvalid() {
    return sticker() && artwork?.quantity && artwork?.quantity < minStickerQuantity();
  }

  function isGenericErrorVisible() {
    return withError() || isMinQuantityInvalid();
  }

  function headerIcon() {
    return isGenericErrorVisible() ? "strong-info" : "check-circle-bold";
  }

  function uploadTimeMessage() {
    return isSpangle()
      ? `Creating your spangle design! Please allow <b>up to ${t(
          "minute",
          remainingTime()
        )}</b> to get your spangle design.`
      : `Proofing your image and making it print-ready. Please allow <b>up to ${t(
          "minute",
          remainingTime()
        )}</b> to get your print-ready image.`;
  }

  function warningMessage() {
    return artwork?.metadata?.warnings?.human;
  }

  function reuploadTitle() {
    return printAsIs() ? "Re-upload a gang sheet in the correct size" : "Re-upload valid artwork";
  }

  function reuploadButton() {
    return printAsIs() ? "Re-upload a gang sheet" : "Choose file";
  }

  function showReuploadBox() {
    return !sizeErrorTypes().includes(artwork.errorType?.toString()) && withError();
  }

  function fileSize() {
    return Math.floor((artwork?.file?.size || 0) * 0.000001 || 0);
  }

  function remainingTime() {
    const spangleTime = isSpangle() ? 1 : 0;
    if (Math.ceil(fileSize() / 10) < 1) {
      return 1 + spangleTime;
    }
    return Math.min(Math.ceil(fileSize() / 10), sizeMessageMaxSize) + spangleTime;
  }

  function showUploadTimeMessage() {
    return artwork.isLoading;
  }

  function isArtworkUploaded() {
    const artworkId = parseInt(artwork?.id, 10);
    if (Number.isNaN(artworkId)) {
      return false;
    }
    return typeof artworkId === "number" && !artwork?.isLoading;
  }

  function disableCustomSizeInput() {
    return isStandardArtwork() && !artwork?.enhancementCompleted;
  }

  function showRedrawNote() {
    return !isStandardArtwork() && showRedrawUI();
  }

  function displayEnhancementFeedback() {
    return (
      enableAbAiMonetization() &&
      artwork.id &&
      artwork.enhancementCompleted &&
      artwork.enhancementTier === "full_enhancements"
    );
  }

  useEffect(() => {
    const val = lastConfirmedConfigSection();
    if (val && String(val.artworkId) === String(artwork?.id)) {
      setActiveConfigSection(val.section);
    }
  }, [artwork?.id]);

  function addFilesToQueue(...args) {
    return dtfStore.addFilesToQueue(...args);
  }

  function updateArtworkValuesById(...args) {
    return dtfStore.updateArtworkValuesById(...args);
  }

  function getArtworksPricing(...args) {
    return dtfStore.getArtworksPricing(...args);
  }

  function handleDesignServiceClick() {
    if (!artwork.hasExpertEditAddon) {
      updateArtworkValuesById({
        id: artwork.id,
        hasExpertEditAddon: true,
        enhancementNote: ""
      });
      getArtworksPricing();
    }
    queueMicrotask(() => {
      const customNotesTextarea = rootRef.current?.querySelector(".js-custom-notes-textarea");
      if (customNotesTextarea) {
        customNotesTextarea.focus();
        customNotesTextarea.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      } else if (redrawNotesRef.current) {
        /* TODO(vue-migration): redrawNotesRef.current.focusTextarea() requires useImperativeHandle on RedrawNotes child component */
        redrawNotesRef.current.focusTextarea?.();
      }
    });
  }

  function onDimensionsChange({ height: h, width: w }) {
    setHeight(h);
    setWidth(w);
  }

  // eslint-disable-next-line no-shadow
  function onQtyChange(quantity) {
    // eslint-disable-line no-shadow
    setQuantity(quantity);
  }

  function trackAddFileAreaClick() {
    submitDtfEvent("replace_artwork_file_clicked", {
      page: "pdp",
      pdp_type: pdpType()
    });
  }

  function uploadClick() {
    trackAddFileAreaClick();
    fileRef.current?.click();
  }

  function handleFileChange(event) {
    const { files } = event.target;
    if (!files.length) return;
    addFilesToQueue({
      files,
      isDropEvent: false,
      artworkIndex: index
    });
    fileRef.current.value = "";
  }

  function handleSetActiveConfigSection(section) {
    setActiveConfigSection(section);
  }

  return (
    <div
      ref={rootRef}
      {...restProps}
      className={cn(
        "artwork scope-f5593a09",
        isGenericErrorVisible() && "artwork--with-error",
        restProps?.className
      )}
    >
      <input ref={fileRef} type="file" style={{ display: "none" }} onChange={handleFileChange} />
      {artwork ? (
        <div
          className={cn(
            "artwork__header scope-f5593a09",
            isGenericErrorVisible() && "artwork__header--with-error"
          )}
        >
          <div className="artwork__header-icon-wrapper scope-f5593a09">
            {!!metadata() || isGenericErrorVisible() ? (
              <UseSvg
                className="artwork__header-icon scope-f5593a09"
                icon={headerIcon()}
                data-testid="artwork-header-icon"
              />
            ) : null}
          </div>
          <span className="artwork__header-text scope-f5593a09">{artwork.name}</span>
        </div>
      ) : null}
      <div className="artwork__content scope-f5593a09">
        <ArtworkInfo
          className="artwork__sizes scope-f5593a09"
          isMultiUpload={isMultiUpload}
          artwork={artwork}
          index={index}
          data-testid="artwork-sizes"
        />

        <div className="scope-f5593a09">
          <ArtworkImage
            className="artwork__image scope-f5593a09"
            index={index}
            artwork={artwork}
            isMultiUpload={isMultiUpload}
            activeConfigSection={activeConfigSection}
            showFullScreenButton={showFullScreenButton}
            data-testid="artwork-image"
          />

          {isStandardArtwork() && !hasErrorMessage() && hasArtwork() ? (
            <div className="artwork__quality-config scope-f5593a09">
              {displayEnhancementFeedback() && paidExpertEditAddonEnabled() && !isMultiUpload ? (
                <DesignServicePromo
                  className="scope-f5593a09"
                  onDesignServiceClicked={handleDesignServiceClick}
                  data-testid="design-service-promo"
                />
              ) : null}
              {displayEnhancementFeedback() && !isMultiUpload ? (
                <ArtworkEnhancementFeedback
                  className="scope-f5593a09"
                  artwork={artwork}
                  pageType={pdpType()}
                  data-testid="artwork-enhancement-feedback"
                />
              ) : null}
              {enableAbAiMonetization() ? (
                <ArtworkQualityConfigPanelAbAiMonetization
                  className="scope-f5593a09"
                  index={index}
                  artwork={artwork}
                  reorderEnabled={reorderEnabled()}
                  activeConfigSection={activeConfigSection}
                  paidExpertEditAddonEnabled={paidExpertEditAddonEnabled()}
                  isMultiUpload={isMultiUpload}
                  onSetActiveConfigSection={handleSetActiveConfigSection}
                  data-testid="artwork-quality-config-panel-ab-ai-monetization"
                />
              ) : (
                <ArtworkQualityConfigPanel
                  className="scope-f5593a09"
                  index={index}
                  artwork={artwork}
                  activeConfigSection={activeConfigSection}
                  isMultiUpload={isMultiUpload}
                  onSetActiveConfigSection={handleSetActiveConfigSection}
                  data-testid="artwork-quality-config-panel"
                />
              )}
            </div>
          ) : null}
        </div>
        <div
          className={cn(
            "artwork__info scope-f5593a09",
            showMultiSize() && "artwork__info--multi-size-presets"
          )}
        >
          {isArtworkUploaded() && isSticker() && experiments().stickers_style_options ? (
            <StickersTabs
              className="artwork__tabs scope-f5593a09"
              tabs={TABS}
              artwork={artwork}
              slots={{
                default: ({ tab }) => (
                  <>
                    {}
                    {tab === "background" ? (
                      <StickersToolsBackground
                        className="artwork__section scope-f5593a09"
                        artwork={artwork}
                        data-testid="artwork-section"
                      />
                    ) : tab === "border" ? (
                      <StickersToolsBorder
                        className="artwork__section scope-f5593a09"
                        artwork={artwork}
                        data-testid="artwork-section-1"
                      />
                    ) : null}
                  </>
                )
              }}
              data-testid="artwork-tabs"
            />
          ) : null}
          <ArtworkInfo
            className="artwork__sizes artwork__sizes--desktop scope-f5593a09"
            artwork={artwork}
            isMultiUpload={isMultiUpload}
            index={index}
            data-testid="artwork-sizes-1"
          />

          {!withError() && warningMessage() ? (
            <ArtworkNotification className="scope-f5593a09" data-testid="artwork-notification">
              <b className="scope-f5593a09">{warningMessage()}</b>
            </ArtworkNotification>
          ) : null}
          {hasErrorMessage() ? (
            <ArtworkError
              className="scope-f5593a09"
              artwork={artwork}
              currentStore={currentStore}
              data-testid="artwork-error"
            />
          ) : null}
          {showRedrawNote() ? (
            <RedrawNotes
              className="scope-f5593a09"
              ref={redrawNotesRef}
              index={index}
              enhancementNote={artwork.enhancementNote}
              isMultiUpload={isMultiUpload}
              data-testid="redraw-notes"
            />
          ) : null}
          {showReuploadBox() ? (
            <ReuploadBox className="scope-f5593a09" index={index} data-testid="reupload-box" />
          ) : (
            <div className="scope-f5593a09">
              {showMultiSize() ? (
                <>
                  <h3 className="artwork__multi-size-title scope-f5593a09">Set Design Size</h3>
                  <ArtworkMultiDimensions
                    className="scope-f5593a09"
                    artwork={artwork}
                    index={index}
                    disabled={artwork.isFile || disableCustomSizeInput()}
                    readonly={printAsIs() && !!metadata()}
                    isMultiUpload={isMultiUpload}
                    showFreeTransfersBadge={showFreeTransfersBadge}
                    data-testid="artwork-multi-dimensions"
                  />

                  <MultiSizeTotal
                    className="scope-f5593a09"
                    index={index}
                    isMultiUpload={isMultiUpload}
                    data-testid="multi-size-total"
                  />
                </>
              ) : (
                <>
                  <ArtworkDimensions
                    className="scope-f5593a09"
                    artwork={artwork}
                    index={index}
                    disabled={artwork.isFile || disableCustomSizeInput()}
                    readonly={printAsIs() && !!metadata()}
                    currentStore={currentStore}
                    isMultiUpload={isMultiUpload}
                    onOnChange={onDimensionsChange}
                    data-testid="artwork-dimensions"
                  />

                  <ArtworkQuantity
                    className="artwork__qty scope-f5593a09"
                    artwork={artwork}
                    index={index}
                    disabled={artwork.isFile}
                    readonly={printAsIs() && !!metadata()}
                    isMultiUpload={isMultiUpload}
                    onChange={onQtyChange}
                    data-testid="artwork-qty"
                  />
                </>
              )}
              {isArtworkUploaded() && !isStandardArtwork() ? (
                <TrimInfo
                  className="artwork__trim-info scope-f5593a09"
                  id={artwork.id}
                  artwork={artwork}
                  canRemoveBg={artwork.canRemoveBg}
                  removeBackground={artwork.removeBackground}
                  toggleBackgroundError={artwork.toggleBackgroundError}
                  reverse={true}
                  data-testid="artwork-trim-info"
                />
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
